<!--
  A headline figure.

  V1 used these across its statistics pages and they worked, so V2 keeps the pattern: an
  icon, a quiet label, one large number, and a delta against the previous period.

  The delta is the part that needs care. It is coloured by direction and it also carries an
  arrow and the words "up" or "down" in its label, because a number that is only red or
  green is meaningless to a viewer who cannot separate the two. And a delta is only shown
  when there is a real previous period to compare against: a "+100%" against an empty week
  is noise presented as insight.
-->
<script setup lang="ts">
import { computed } from 'vue';
// Aliased: a prop is also called `icon`, and in <script setup> both would be in
// template scope under the same name.
import { icon as resolveIcon } from '../icons.js';

const props = withDefaults(
  defineProps<{
    label: string;
    value: string | number;
    icon?: string;
    /** Small print under the value, for the definition or the window. */
    note?: string | null;
    /** Percentage change against the previous period. */
    deltaPercent?: number | null;
    /** What the delta is measured against, e.g. "vs. previous 30 days". */
    deltaLabel?: string | null;
    /** For a metric where a fall is good, such as silence seconds. */
    lowerIsBetter?: boolean;
    tone?: 'neutral' | 'accent' | 'warning' | 'danger';
  }>(),
  { tone: 'neutral', lowerIsBetter: false },
);

const IconComponent = computed(() => (props.icon ? resolveIcon(props.icon) : null));

const delta = computed(() => {
  if (props.deltaPercent === null || props.deltaPercent === undefined) return null;
  const rising = props.deltaPercent > 0;
  const flat = Math.abs(props.deltaPercent) < 0.05;

  return {
    flat,
    rising,
    good: flat ? null : rising !== props.lowerIsBetter,
    text: `${rising ? '+' : ''}${props.deltaPercent.toFixed(1)}%`,
    // Spelled out for a screen reader, and for anyone reading the arrow as decoration.
    spoken: flat
      ? 'unchanged'
      : `${rising ? 'up' : 'down'} ${Math.abs(props.deltaPercent).toFixed(1)} percent`,
  };
});
</script>

<template>
  <div class="shio-stat" :class="`shio-stat--${tone}`">
    <p class="shio-stat__label">
      <component :is="IconComponent" v-if="IconComponent" :size="14" aria-hidden="true" />
      <span>{{ label }}</span>
    </p>

    <p class="shio-stat__value">{{ value }}</p>

    <p v-if="delta" class="shio-stat__delta">
      <span
        class="shio-stat__chip"
        :class="{
          'shio-stat__chip--good': delta.good === true,
          'shio-stat__chip--bad': delta.good === false,
        }"
      >
        <span aria-hidden="true">{{ delta.flat ? '→' : delta.rising ? '↗' : '↘' }}</span>
        <span>{{ delta.text }}</span>
        <span class="shio-sr-only">{{ delta.spoken }}</span>
      </span>
      <span v-if="deltaLabel" class="shio-stat__note">{{ deltaLabel }}</span>
    </p>

    <p v-else-if="note" class="shio-stat__note">{{ note }}</p>
  </div>
</template>

<style scoped>
.shio-stat {
  display: flex;
  flex-direction: column;
  gap: var(--shio-space-1);
  padding: var(--shio-space-4);
  border: 1px solid hsl(var(--shio-border));
  border-radius: var(--shio-radius-md);
  background: hsl(var(--shio-surface-elevated));
}

.shio-stat--accent {
  border-color: hsl(var(--shio-accent-primary) / 0.4);
}

.shio-stat--warning {
  border-color: hsl(var(--shio-status-warning) / 0.5);
}

.shio-stat--danger {
  border-color: hsl(var(--shio-status-danger) / 0.5);
}

.shio-stat__label {
  display: flex;
  align-items: center;
  gap: var(--shio-space-2);
  margin: 0;
  font-family: var(--shio-font-body);
  font-size: var(--shio-type-body-sm-size);
  color: hsl(var(--shio-text-muted));
}

.shio-stat__value {
  margin: 0;
  font-family: var(--shio-font-display);
  font-size: var(--shio-type-display-size);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: hsl(var(--shio-text-primary));
  line-height: 1.1;
}

.shio-stat__delta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--shio-space-2);
  margin: 0;
}

.shio-stat__chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px var(--shio-space-2);
  border-radius: var(--shio-radius-sm);
  background: hsl(var(--shio-surface-base));
  font-family: var(--shio-font-body);
  font-size: var(--shio-type-meta-size);
  font-variant-numeric: tabular-nums;
  color: hsl(var(--shio-text-secondary));
}

.shio-stat__chip--good {
  color: hsl(var(--shio-status-success));
}

.shio-stat__chip--bad {
  color: hsl(var(--shio-status-danger-strong));
}

.shio-stat__note {
  margin: 0;
  font-family: var(--shio-font-body);
  font-size: var(--shio-type-meta-size);
  color: hsl(var(--shio-text-muted));
}
</style>
